import { useEffect, useState } from 'react';
import type { KlientDeal, KlientDealInput, KlientDealInvoiceGroup, KlientDealKind, KlientDealStatus } from '../api/klienti';
import { confirmDialog } from '../shell/dialogs';
import { useFeather } from '../shell/useFeather';

function kMoney(n: number, currency: string): string {
  const v = new Intl.NumberFormat('cs-CZ').format(Math.round(n || 0));
  return currency === 'EUR' ? `${v} €` : `${v} Kč`;
}
function kPct1(n: number): string { return new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(n || 0); }

const DEAL_KIND_LABEL: Record<KlientDealKind, string> = {
  fix: 'Fixní položka',
  affiliate_passthrough: 'Přefakturace provizí affiliatů',
  rs_fee: 'Provize z NGR',
  cpa_fee: 'Provize z CPA',
  reg_fee: 'Provize za uznanou registraci',
};
const DEAL_STATUS_LABEL: Record<KlientDealStatus, string> = { pripraveno: 'Připraveno', aktivni: 'Aktivní', ukonceny: 'Ukončený' };
type DealForm = { kind: KlientDealKind; title: string; amount: string; percent: string; currency: 'CZK' | 'EUR'; exchangeRate: string; invoiceGroup: KlientDealInvoiceGroup; uolProductId: string; validFrom: string; validTo: string; status: KlientDealStatus; note: string };
const DEAL_DEFAULT_GROUP: Record<KlientDealKind, KlientDealInvoiceGroup> = { fix: 'fixni', affiliate_passthrough: 'provize', rs_fee: 'provize', cpa_fee: 'provize', reg_fee: 'provize' };
const DEAL_DEFAULT_PRODUCT: Record<KlientDealKind, string> = { fix: 'affiliate_service', affiliate_passthrough: 'affiliate_revenue', rs_fee: 'affiliate_service', cpa_fee: 'affiliate_service', reg_fee: 'affiliate_service' };

function dealValue(dl: KlientDeal): string {
  if (dl.kind === 'fix') return dl.amount == null ? '-' : `${kMoney(dl.amount, dl.currency)} bez DPH / měsíc`;
  if (dl.kind === 'rs_fee') return dl.percent == null ? '-' : `${kPct1(dl.percent)} % z NGR`;
  if (dl.kind === 'cpa_fee') return dl.percent == null ? '-' : `${kPct1(dl.percent)} % z CPA provizí`;
  if (dl.kind === 'reg_fee') return dl.amount == null ? '-' : `${kMoney(dl.amount, dl.currency)} za uznanou registraci`;
  return dl.exchangeRate == null ? 'řádek per affiliate' : `řádek per affiliate, kurz ${dl.exchangeRate} CZK/EUR`;
}

function dealPeriod(dl: KlientDeal): string {
  const f = (v: string | null) => (v ? `${v.slice(8, 10)}.${v.slice(5, 7)}.${v.slice(0, 4)}` : null);
  const a = f(dl.validFrom); const b = f(dl.validTo);
  if (!a && !b) return 'účtovat od: nezadáno';
  if (a && b) return `${a} – ${b}`;
  return a ? `od ${a}` : `od: nezadáno, do ${b}`;
}

function dealMissingStart(dl: KlientDeal): boolean {
  return !dl.validFrom && dl.status !== 'ukonceny';
}

export interface KlientDealyApi {
  list: (partnerId: number) => Promise<KlientDeal[]>;
  add: (partnerId: number, input: KlientDealInput) => Promise<unknown>;
  update: (partnerId: number, dealId: number, input: KlientDealInput) => Promise<unknown>;
  setStatus: (partnerId: number, dealId: number, status: KlientDealStatus) => Promise<unknown>;
}

interface KlientDealyProps {
  id: number;
  defaultCurrency: 'CZK' | 'EUR';
  canManage: boolean;
  api: KlientDealyApi;
  errText: (e: unknown, fallback: string) => string;
  kinds?: KlientDealKind[];
  fixProduct?: string;
}

const ALL_KINDS = Object.keys(DEAL_KIND_LABEL) as KlientDealKind[];

export function KlientDealy({ id, defaultCurrency, canManage, api, errText, kinds = ALL_KINDS, fixProduct = DEAL_DEFAULT_PRODUCT.fix }: KlientDealyProps) {
  const single = kinds.length === 1;
  const [rows, setRows] = useState<KlientDeal[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [form, setForm] = useState<DealForm | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [formErr, setFormErr] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    api.list(id)
      .then((d) => { if (!cancelled) setRows(d); })
      .catch((e: unknown) => { if (!cancelled) setError(errText(e, 'Načtení dealů selhalo')); });
    return () => { cancelled = true; };
  }, [id, reload, api, errText]);
  useFeather([rows, form]);
  const emptyForm = (): DealForm => ({ kind: 'fix', title: '', amount: '', percent: '', currency: defaultCurrency, exchangeRate: '', invoiceGroup: 'fixni', uolProductId: fixProduct, validFrom: '', validTo: '', status: 'aktivni', note: '' });
  function openAdd() { setEditId(null); setFormErr(null); setForm(emptyForm()); }
  function openEdit(dl: KlientDeal) {
    setEditId(dl.id); setFormErr(null);
    setForm({ kind: dl.kind, title: dl.title, amount: dl.amount == null ? '' : String(dl.amount), percent: dl.percent == null ? '' : String(dl.percent), currency: dl.currency === 'EUR' ? 'EUR' : 'CZK', exchangeRate: dl.exchangeRate == null ? '' : String(dl.exchangeRate), invoiceGroup: dl.invoiceGroup, uolProductId: dl.uolProductId ?? '', validFrom: dl.validFrom ?? '', validTo: dl.validTo ?? '', status: dl.status, note: dl.note ?? '' });
  }
  function changeKind(kind: KlientDealKind) {
    if (!form) return;
    const invoiceGroup = DEAL_DEFAULT_GROUP[kind];
    setForm({ ...form, kind, invoiceGroup, uolProductId: form.uolProductId && form.uolProductId !== DEAL_DEFAULT_PRODUCT[form.kind] ? form.uolProductId : DEAL_DEFAULT_PRODUCT[kind] });
  }
  async function save() {
    if (!form) return;
    if (form.title.trim().length < 2) { setFormErr('Název musí mít aspoň 2 znaky'); return; }
    if ((form.kind === 'fix' || form.kind === 'reg_fee') && !form.amount.trim()) { setFormErr('Zadej částku'); return; }
    if ((form.kind === 'rs_fee' || form.kind === 'cpa_fee') && !form.percent.trim()) { setFormErr('Zadej sazbu v %'); return; }
    setBusy(true); setFormErr(null);
    try {
      const p = { kind: form.kind, title: form.title.trim(), amount: form.amount.trim() || null, percent: form.percent.trim() || null, currency: form.currency, exchangeRate: form.exchangeRate.trim() || null, invoiceGroup: form.invoiceGroup, uolProductId: form.uolProductId.trim() || null, validFrom: form.validFrom || null, validTo: form.validTo || null, status: form.status, note: form.note.trim() || null };
      if (editId == null) await api.add(id, p);
      else await api.update(id, editId, p);
      setForm(null); setReload((r) => r + 1);
    } catch (e) { setFormErr(errText(e, 'Uložení selhalo')); }
    finally { setBusy(false); }
  }
  async function changeStatus(dl: KlientDeal, status: KlientDealStatus) {
    if (status === 'ukonceny' && !(await confirmDialog({ title: 'Ukončit deal?', message: `Deal „${dl.title}“ přejde do stavu Ukončený.`, confirmLabel: 'Ukončit', danger: true }))) return;
    if (status === 'aktivni' && !dl.validFrom) {
      openEdit(dl);
      setForm((f) => (f ? { ...f, status: 'aktivni' } : f));
      setFormErr('Doplň „Účtovat od“, bez něj nejde deal aktivovat.');
      return;
    }
    try { await api.setStatus(id, dl.id, status); setReload((r) => r + 1); }
    catch (e) { setError(errText(e, 'Změna stavu selhala')); }
  }
  if (error) return <div className="alert alert-danger mb-0">{error}</div>;
  if (!rows) return <div className="text-muted py-3"><span className="spinner-border spinner-border-sm me-2" /> Načítám…</div>;
  const showPercent = form?.kind === 'rs_fee' || form?.kind === 'cpa_fee';
  const showAmount = form?.kind === 'fix' || form?.kind === 'reg_fee';
  const showRate = form?.kind !== 'fix' && form?.kind !== 'reg_fee';
  return (
    <section>
      {canManage && (
        <div className="dealy-toolbar">
          <button type="button" className="btn btn-primary btn-sm" onClick={openAdd}><i data-feather="plus" style={{ width: 14, height: 14 }} /> Přidat deal</button>
        </div>
      )}
      {rows.length === 0 ? <div className="prototype-empty-contact">Zatím žádné dealy.</div> : (
        <div className="dealy-vypis">
          {(['fixni', 'provize'] as KlientDealInvoiceGroup[]).map((group) => {
            const list = rows.filter((r) => r.invoiceGroup === group);
            if (list.length === 0) return null;
            const fixSum = list.filter((r) => r.status === 'aktivni' && r.kind === 'fix' && r.amount != null).reduce((a, r) => a + (r.amount ?? 0), 0);
            const fixCurrency = list.find((r) => r.kind === 'fix')?.currency ?? defaultCurrency;
            return (
              <section className="dealy-skupina" key={group}>
                <header className="dealy-skupina-head">
                  <span className="dealy-skupina-nazev">{single ? 'Fixní položky' : group === 'fixni' ? 'Faktura 1 · Fixní' : 'Faktura 2 · Provize'}</span>
                  <span className="dealy-skupina-pocet">{list.length === 1 ? '1 deal' : `${list.length} dealy`}</span>
                  {group === 'fixni' && fixSum > 0 && <span className="dealy-skupina-suma">{kMoney(fixSum, fixCurrency)} bez DPH / měsíc</span>}
                </header>
                <ol className="dealy-vetev">
                  {list.map((dl) => (
                    <li className={`dealy-radek${dl.status === 'ukonceny' ? ' je-ukonceny' : ''}`} key={dl.id}>
                      <span className="dealy-nazev">
                        {!single && <em className="dealy-typ">{DEAL_KIND_LABEL[dl.kind]}</em>}
                        {dl.title}
                        {dl.note && <small className="dealy-poznamka">{dl.note}</small>}
                      </span>
                      <span className="dealy-hodnota">{dealValue(dl)}</span>
                      <span className="dealy-produkt">{dl.uolProductId ?? '-'}</span>
                      <span className={`dealy-platnost${dealMissingStart(dl) ? ' je-bez-data' : ''}`} title={dealMissingStart(dl) ? 'Deal nemá nastavené Účtovat od' : undefined}>{dealPeriod(dl)}</span>
                      <span className={`dealy-stav je-${dl.status}`}>{DEAL_STATUS_LABEL[dl.status]}</span>
                      {canManage ? (
                        <span className="dealy-akce">
                          <button type="button" onClick={() => openEdit(dl)} title={`Upravit ${dl.title}`} aria-label={`Upravit ${dl.title}`}><i data-feather="edit-2" /></button>
                          {dl.status === 'pripraveno' && <button type="button" onClick={() => void changeStatus(dl, 'aktivni')} title={`Aktivovat ${dl.title}`} aria-label={`Aktivovat ${dl.title}`}><i data-feather="play-circle" /></button>}
                          {dl.status === 'aktivni' && <button type="button" className="is-danger" onClick={() => void changeStatus(dl, 'ukonceny')} title={`Ukončit ${dl.title}`} aria-label={`Ukončit ${dl.title}`}><i data-feather="x-circle" /></button>}
                          {dl.status === 'ukonceny' && <button type="button" onClick={() => void changeStatus(dl, 'aktivni')} title={`Obnovit ${dl.title}`} aria-label={`Obnovit ${dl.title}`}><i data-feather="rotate-ccw" /></button>}
                        </span>
                      ) : <span />}
                    </li>
                  ))}
                </ol>
              </section>
            );
          })}
        </div>
      )}
      {form && (
        <div className="prototype-modal-backdrop" onClick={() => setForm(null)}>
          <div onClick={(e) => e.stopPropagation()} className="card prototype-modal-card" style={{ width: '100%', maxWidth: 560 }}>
            <div className="prototype-modal-head"><div><div className="prototype-eyebrow">DEAL</div><h2>{editId == null ? 'Přidat deal' : 'Upravit deal'}</h2></div><button type="button" className="prototype-modal-close" aria-label="Zavřít" onClick={() => setForm(null)}>×</button></div>
            <div className="card-body">
              {formErr && <div className="alert alert-danger py-2">{formErr}</div>}
              <div className="row g-2">
                {!single && <div className="col-md-6"><label className="form-label mb-1" style={{ fontSize: '0.82rem' }}>Typ dealu *</label>
                  <select className="form-select form-select-sm" value={form.kind} onChange={(e) => changeKind(e.target.value as KlientDealKind)}>
                    {kinds.map((k) => <option key={k} value={k}>{DEAL_KIND_LABEL[k]}</option>)}
                  </select></div>}
                {!single && <div className="col-md-6"><label className="form-label mb-1" style={{ fontSize: '0.82rem' }}>Faktura *</label>
                  <select className="form-select form-select-sm" value={form.invoiceGroup} onChange={(e) => setForm({ ...form, invoiceGroup: e.target.value as KlientDealInvoiceGroup })}>
                    <option value="fixni">1 – Fixní</option><option value="provize">2 – Provize</option>
                  </select></div>}
                <div className="col-12"><label className="form-label mb-1" style={{ fontSize: '0.82rem' }}>Název *</label>
                  <input className="form-control form-control-sm" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={form.kind === 'fix' ? 'např. VIP instance' : form.kind === 'rs_fee' ? 'např. Provize z NGR' : form.kind === 'cpa_fee' ? 'např. Provize z CPA' : form.kind === 'reg_fee' ? 'např. Provize za registraci' : 'např. Affiliate Commission'} /></div>
                {showAmount && <div className="col-md-4"><label className="form-label mb-1" style={{ fontSize: '0.82rem' }}>{form.kind === 'reg_fee' ? 'Částka za registraci *' : 'Částka bez DPH *'}</label>
                  <input className="form-control form-control-sm" inputMode="decimal" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>}
                {showPercent && <div className="col-md-4"><label className="form-label mb-1" style={{ fontSize: '0.82rem' }}>Sazba % *</label>
                  <input className="form-control form-control-sm" inputMode="decimal" value={form.percent} onChange={(e) => setForm({ ...form, percent: e.target.value })} /></div>}
                <div className="col-md-4"><label className="form-label mb-1" style={{ fontSize: '0.82rem' }}>Měna</label>
                  <select className="form-select form-select-sm" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value as 'CZK' | 'EUR' })}><option>CZK</option><option>EUR</option></select></div>
                {showRate && <div className="col-md-4"><label className="form-label mb-1" style={{ fontSize: '0.82rem' }}>Kurz CZK/EUR</label>
                  <input className="form-control form-control-sm" inputMode="decimal" value={form.exchangeRate} onChange={(e) => setForm({ ...form, exchangeRate: e.target.value })} placeholder="např. 25" /></div>}
                <div className="col-md-4"><label className="form-label mb-1" style={{ fontSize: '0.82rem' }}>UOL produkt</label>
                  <input className="form-control form-control-sm" value={form.uolProductId} onChange={(e) => setForm({ ...form, uolProductId: e.target.value })} /></div>
                <div className="col-md-4"><label className="form-label mb-1" style={{ fontSize: '0.82rem' }}>Účtovat od</label>
                  <input type="date" className="form-control form-control-sm" value={form.validFrom} onChange={(e) => setForm({ ...form, validFrom: e.target.value })} /></div>
                <div className="col-md-4"><label className="form-label mb-1" style={{ fontSize: '0.82rem' }}>Účtovat do</label>
                  <input type="date" className="form-control form-control-sm" value={form.validTo} onChange={(e) => setForm({ ...form, validTo: e.target.value })} /></div>
                <div className="col-md-4"><label className="form-label mb-1" style={{ fontSize: '0.82rem' }}>Stav</label>
                  <select className="form-select form-select-sm" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as KlientDealStatus })}>
                    {(Object.keys(DEAL_STATUS_LABEL) as KlientDealStatus[]).map((k) => <option key={k} value={k}>{DEAL_STATUS_LABEL[k]}</option>)}
                  </select></div>
                <div className="col-md-8"><label className="form-label mb-1" style={{ fontSize: '0.82rem' }}>Poznámka</label>
                  <input className="form-control form-control-sm" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
              </div>
            </div>
            <div className="card-footer prototype-modal-actions">
              <button type="button" className="btn prototype-secondary-btn" disabled={busy} onClick={() => setForm(null)}>Zrušit</button>
              <button type="button" className="btn btn-primary prototype-primary-btn" disabled={busy} onClick={save}>{busy ? 'Ukládám…' : 'Uložit'}</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
